import { ArrowDown, ArrowUp, ExternalLink } from "@/components/ui/icons";
import { type ReactNode, useState } from "react";
import { Text } from "react-native";

import { Card } from "@/components/ui/Card";
import { formatMoney } from "@/lib/money/money";
import { useThemeColors } from "@/theme/theme";

import {
  FLAGS,
  type RecentSale,
  saleDate,
  type SaleSort,
  sharedContext,
  sortSales,
  SourceTabs,
} from "./RecentSales.shared";

const CELL = "whitespace-nowrap px-3 py-2.5 align-middle first:pl-0 last:pr-0";
const HEAD =
  "px-3 pb-2 first:pl-0 last:pr-0 font-label text-[11px] font-normal uppercase tracking-widest text-subtle";
/** Sales shown before "Show all", matching the phone list. */
const VISIBLE = 8;

/** A column header that sorts its column, and says which way it is sorted. */
function SortHead(
  { label, column, sort, onSort, align }: {
    label: string;
    column: SaleSort["key"];
    sort: SaleSort;
    onSort: (column: SaleSort["key"]) => void;
    align: "left" | "right";
  },
) {
  const c = useThemeColors();
  const active = sort.key === column;
  const Arrow = sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className={`${HEAD} ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 uppercase tracking-widest ${
          active ? "text-foreground" : "text-subtle"
        } hover:text-foreground`}
      >
        {label}
        {active ? <Arrow size={11} color={c.foreground} aria-hidden /> : null}
      </button>
    </th>
  );
}

/**
 * Recent sales on the desktop Dossier: a table of what sold, with the search that finds more.
 *
 * It states a grade or condition once when every sale shares it, rather than on each row, and its
 * Copies column appears only for sources that report a quantity. With no sales it says why in one
 * quiet line; the eBay search stays where it is, because that is the next thing to try.
 */
export function RecentSalesTable(
  { sales, median, emptyMessage, capturedAt, refreshFailed, action }: {
    sales: RecentSale[];
    median?: { price: string; count: number; sources: string[]; };
    emptyMessage?: string;
    capturedAt?: string | null;
    refreshFailed?: boolean;
    action?: ReactNode;
  },
) {
  const c = useThemeColors();
  const [expanded, setExpanded] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [sort, setSort] = useState<SaleSort>({ key: "date", direction: "desc" });
  const sources = [...new Set(sales.map((sale) => sale.source))].sort();
  const selected = source && sources.includes(source) ? source : null;
  const filtered = selected ? sales.filter((sale) => sale.source === selected) : sales;
  const ordered = sortSales(filtered, sort);
  const visible = expanded ? ordered : ordered.slice(0, VISIBLE);
  const context = sharedContext(sales);
  const showCopies = sales.some((sale) => sale.quantity != null);
  const showFlag = sales.some((sale) => sale.flag);
  const onSort = (column: SaleSort["key"]) =>
    setSort((now) => ({
      key: column,
      direction: now.key === column && now.direction === "desc" ? "asc" : "desc",
    }));
  const notes = [
    capturedAt ? `Marketplace sales checked ${saleDate(capturedAt)}` : null,
    refreshFailed && sales.length > 0
      ? "Showing saved sales while the latest update is unavailable."
      : null,
  ].filter((note): note is string => !!note);

  return (
    <Card className="gap-4" testID="recent-sales">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex flex-col gap-1">
          <Text
            accessibilityRole="header"
            aria-level={2}
            className="font-title text-lg text-foreground"
          >
            Recent sales
          </Text>
          <Text className="font-body text-xs text-muted-foreground">
            {context ? `${context} · USD per copy` : "USD per copy"}
          </Text>
        </div>
        {action}
      </div>
      {median && !selected
        ? (
          <div
            role="group"
            className="flex flex-wrap items-baseline gap-x-2"
            aria-label={`Recent median ${
              formatMoney(median.price)
            }, based on ${median.count} sales from ${median.sources.join(", ")}`}
          >
            <Text className="font-label text-[13px] text-muted-foreground">Recent median</Text>
            <Text className="font-num-bold text-lg text-foreground">
              {formatMoney(median.price)}
            </Text>
            <Text className="font-body text-xs text-subtle">
              {`${median.count} sales · ${median.sources.join(", ")} · 90 days`}
            </Text>
          </div>
        )
        : null}
      {notes.map((note) => <Text key={note} className="font-body text-xs text-subtle">{note}
      </Text>)}
      {sales.length === 0
        ? (
          <Text testID="recent-sales-empty" className="font-body text-sm text-muted-foreground">
            {emptyMessage ?? "No recent sales on record for this scope."}
          </Text>
        )
        : (
          <>
            {sources.length > 1
              ? (
                <SourceTabs
                  sales={sales}
                  sources={sources}
                  selected={selected}
                  onSelect={(value) => {
                    setSource(value);
                    setExpanded(false);
                  }}
                />
              )
              : null}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left" aria-label="Recent sales">
                <thead>
                  <tr>
                    <SortHead label="Date" column="date" sort={sort} onSort={onSort} align="left" />
                    <SortHead
                      label="Price"
                      column="price"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    {context ? null : <th scope="col" className={`${HEAD} text-left`}>Grade</th>}
                    <th scope="col" className={`${HEAD} text-left`}>Source</th>
                    {showCopies
                      ? <th scope="col" className={`${HEAD} text-right`}>Copies</th>
                      : null}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((sale) => {
                    const flag = sale.flag ? FLAGS[sale.flag] : null;
                    const date = saleDate(sale.soldAt);
                    return (
                      <tr key={sale.id} className="border-t border-border">
                        <td className={`${CELL} font-body text-[13px] text-muted-foreground`}>
                          {date}
                        </td>
                        <td
                          className={`${CELL} text-right font-num-bold text-[15px] text-foreground`}
                        >
                          {formatMoney(sale.price)}
                        </td>
                        {context
                          ? null
                          : (
                            <td className={`${CELL} font-body text-[13px] text-foreground`}>
                              {sale.context}
                            </td>
                          )}
                        <td className={`${CELL} font-label text-[13px] text-foreground`}>
                          <span className="flex flex-wrap items-center gap-x-2">
                            {sale.url
                              ? (
                                <a
                                  href={sale.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`${sale.source}, sold ${date} for ${
                                    formatMoney(sale.price)
                                  }, open listing`}
                                  className="flex items-center gap-1.5 hover:underline"
                                >
                                  {sale.source}
                                  <ExternalLink size={12} color={c.subtle} aria-hidden />
                                </a>
                              )
                              : sale.source}
                            {showFlag && flag
                              ? (
                                <span
                                  className={`font-label text-[11px] tracking-wide ${flag.textClassName}`}
                                >
                                  {flag.label}
                                </span>
                              )
                              : null}
                          </span>
                        </td>
                        {showCopies
                          ? (
                            <td
                              className={`${CELL} text-right font-num text-[13px] text-muted-foreground`}
                            >
                              {sale.quantity}
                            </td>
                          )
                          : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {visible.length < ordered.length
              ? (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="self-start font-label text-[13px] text-primary hover:underline"
                >
                  {`Show all ${ordered.length} sales`}
                </button>
              )
              : null}
          </>
        )}
    </Card>
  );
}
